import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import apis from '@src/apis';
import actions from '@src/redux/actions';
import route from '@src/constants/route';
import { PROJECT_PRODUCT } from '@src/constants/project';
import { trackProjectCreated } from '@src/services/moengage';

/**
 * Unified create-project flow used by every input variant (FILE / SRT / PPTX
 * / AUDIO / URL).
 *
 * - `createProject` calls the API and returns the raw response. Callers handle
 *   redirect + product wiring themselves so they can branch on backend errors
 *   (e.g. `FEATURE_NOT_IN_PACKAGE`) before navigating.
 * - For DUBBING payloads with `extra.sentences`, the SRT sentences are
 *   dispatched into the dubbingRequest slice BEFORE the API call so the
 *   editor loads with the right state on the next page.
 * - `finalizeProjectDefault` is the standard happy-path: redirect to the
 *   project page and `setProduct` so the layout switches.
 *
 * No internal `submitting` flag — callers wrap the entire submit flow
 * (file processing + upload + API call + finalize) in their own state, so
 * a hook-local flag would only cover the API portion and add bookkeeping.
 */
export const useCreateProjectFromInput = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const createProject = async ({
    product,
    blocks,
    source,
    language,
    extra,
    title,
    sourceType,
  }) => {
    if (product === PROJECT_PRODUCT.DUBBING && extra?.sentences) {
      dispatch(
        actions.dubbingRequest.updateDubbingRequestByKey(
          'sentences',
          extra.sentences,
        ),
      );
    }

    const payload = {
      title,
      product,
      isDeleted: false,
      ...(blocks !== undefined ? { blocks } : {}),
      ...(source ? { source } : {}),
      ...(language ? { language } : {}),
    };

    const res = await apis.project.createProject(payload);
    if (!res?.status) {
      const err = new Error(res?.message || 'createProjectFailed');
      err.code = 'create-failed';
      err.errorCode = res?.errorCode;
      throw err;
    }
    trackProjectCreated({
      product,
      source,
      extra,
      sourceType,
    });
    return { res, projectId: res.result.project.id };
  };

  const finalizeProjectDefault = ({ projectId, product }) => {
    // Match legacy ordering: dispatch product BEFORE navigating so the
    // destination page reads the correct layout slice on mount.
    dispatch(actions.project.setProduct(product));
    history.push(`${route.PROJECTS}/${projectId}`);
  };

  return { createProject, finalizeProjectDefault };
};
